import { css, html, LitElement, nothing } from 'lit';

import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { DPromoDialog } from 'src/library/promo/d-promo-dialog';

import { features } from '../../store/selectors/features/features';
import type { OrganizationViewModelSpecialTermsEnum } from '@trinnvis/dabih-api-client';
import './d-new-documents-list';
import { promo } from 'src/store/selectors/promo';
import type { FeatureStates } from '../../store/selectors/featureStates';
import type { PromoDialogResult } from '../../library/promo/promo-dialog-result';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { PageMenuGroup } from './page-menu-group';

/**
 * Viser en generell + knapp for å legge til nye elementer
 *
 *
 */
@customElement('d-new-document')
export class DNewDocument extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      bottom: 0;
      right: 0;
    }

    #slider {
      display: flex;
      flex-direction: column;
      background: white;
      border-top-left-radius: 12px;
      width: 300px;
      max-height: calc((var(--vh, 1vh) * 100) - 20px);
      box-sizing: border-box;
    }

    .header {
      flex: none;
      font-size: 17px;
      font-weight: 500;
      padding-bottom: 10px;
      border-bottom: 1px solid var(--borderColor);
    }

    .body {
      flex: 1;
      max-height: calc(100vh - 200px);
      overflow: auto;
    }

    .footer {
      flex: none;
    }

    .doc-types-list {
      margin-top: -1px;
      border-top: 1px solid var(--borderColor);
      padding: 4px 0;
    }

    .doc-types-list a {
      display: block;
      margin-top: 1px;
      padding: 6px 0px 6px 30px;
      background-size: 28px 28px;
      background-position: -2px 2px;
      background-repeat: no-repeat;
      opacity: 0.8;
    }

    a {
      color: var(--linkColorGray);
      text-decoration: none;
      cursor: pointer;
    }

    .doc-types-list a:hover {
      color: var(--themeColorDarkerOne);
      opacity: 1;
    }

    @media (hover: hover) {
      .doc-types-list a:hover {
        opacity: 1;
      }
      .issueMessage:hover {
        color: white;
        background: var(--alertColorDarkerOne);
      }

      .feedback:hover {
        color: white;
        background: var(--themeColorDarkerOne);
      }
    }

    .issueMessage,
    .feedback {
      color: white;
      margin-top: 8px;
      padding: 8px 10px;
      border-radius: 6px;
      cursor: pointer;
    }

    .issueMessage {
      background: var(--alertColor);
    }

    .inactive {
      opacity: 0.5;
    }

    .feedback {
      background: var(--themeColor);
      margin-bottom: 8px;
    }

    @media (hover: hover) {
      .issueMessage:hover {
        color: white;
        background: var(--alertColorDarkerOne);
      }

      .feedback:hover {
        color: white;
        background: var(--themeColorDarkerOne);
      }
    }
  `;

  @property({ type: Boolean })
  /**
   * The menu is open
   */
  open = false;
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  currentUserHasEmergencyAccess = false;
  @property({ type: Array })
  currentPathArray: string[] = [];
  @property({ type: Boolean })
  singleUser = false;
  @property({ type: Object })
  featureStates!: FeatureStates;
  @property({ type: Array })
  userEmails: string[] = [];
  @property({ type: Array })
  partners: SelectDropdownOption[] = [];
  @property({ type: Array })
  pageMenu: PageMenuGroup[] = [];
  @property({ type: String })
  defaultFunctionUuid = '';
  @property({ type: Number })
  employeesCount = 0;
  @property({ type: String })
  specialTerms: OrganizationViewModelSpecialTermsEnum | undefined = undefined;

  private get isPerformanceReviewOnly(): boolean {
    return (
      this.featureStates.performanceReview &&
      !this.featureStates.core &&
      !this.featureStates.meetings &&
      !this.featureStates.hse &&
      !this.featureStates.staffing &&
      !this.featureStates.assets &&
      !this.featureStates.substances &&
      !this.featureStates.issues &&
      !this.featureStates.risk &&
      !this.featureStates.data &&
      !this.featureStates.quality &&
      !this.featureStates.healthcare &&
      !this.featureStates.collections
    );
  }

  _sliderClass(open: boolean) {
    if (open) {
      return 'open';
    }
    return '';
  }

  onClickSlider(e) {
    if (e.target.tagName !== 'A') {
      this._toggleSlide();
    }
  }

  _toggleSlide() {
    this.open = !this.open;
  }

  _openFeedback() {
    this.dispatchEvent(new CustomEvent('display-feedback', { composed: true, bubbles: true, detail: {} }));
  }

  _openIssue() {
    this.dispatchEvent(new CustomEvent('display-new-issue', { composed: true, bubbles: true, detail: {} }));
  }

  renderDocList() {
    if (this.writeAccess) {
      return html`<div class="doc-types-list">
        <d-new-documents-list
          .writeAccess=${this.writeAccess}
          .currentUserHasEmergencyAccess=${this.currentUserHasEmergencyAccess}
          .currentPathArray=${this.currentPathArray}
          .singleUser=${this.singleUser}
          .featureStates=${this.featureStates}
          .userEmails=${this.userEmails}
          .partners=${this.partners}
          .pageMenu=${this.pageMenu}
          .defaultFunctionUuid=${this.defaultFunctionUuid}
          .employeesCount=${this.employeesCount}
          .specialTerms=${this.specialTerms}
          .isPerformanceReviewOnly=${this.isPerformanceReviewOnly}
        ></d-new-documents-list>
      </div>`;
    }
    return nothing;
  }
  render() {
    return html`
      <div class="body">${this.renderDocList()}</div>
      <div class="footer">
        <div class="doc-types-list">
          ${(this.featureStates.core || this.featureStates.issues) && !this.isPerformanceReviewOnly
            ? html` <div class="issueMessage" @click=${() => this._openIssue()}>${t('issues.newIssueReport')}</div>`
            : this.isPerformanceReviewOnly
              ? nothing
              : html` <div class="issueMessage inactive" @click=${(e) => this.onClickPromo(e, 'issues')}>
                  ${t('issues.newIssueReport')}
                </div>`}
          <div class="feedback" @click=${() => this._openFeedback()}>${t('layout.feedback.title')}</div>
        </div>
      </div>
    `;
  }

  private async onClickPromo(e, type) {
    e.preventDefault();
    const promoData = promo.find((p) => {
      return p.id === type;
    });
    if (promoData) {
      const result: PromoDialogResult = await DPromoDialog.open({
        promoData,
        features: features,
        employeesCount: this.employeesCount,
        specialTerms: this.specialTerms,
        healthcareSignatoryOptions: [],
        healthcareSignatory: '',
        canUpgrade: this.writeAccess,
        hasHealthcare: this.featureStates.healthcare,
      });
      if (result.action === 'upgrade') {
        console.log('upgrade');
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-new-document': DNewDocument;
  }
}
